import type { AnimationI, EaseT } from "tweenly"
import Box from "@mui/material/Box"
import { EaseStyles, defaultEase } from "../../../../data/styles/ease.styles"
import ExpandableSection from "../../../common/ExpandableSection"
import CompactSelect, { CompactSelectOptionT } from "../../../common/PropertyTools/CompactSelect"
import PropertyRow from "../../../common/PropertyTools/PropertyRow"

interface Props {
    animation: AnimationI
    selectedAnimations: AnimationI[]
    handleSetValue: (property: any, value: any) => void
    handleSetValues: (animations: AnimationI[], property: any, value: any) => void
    handleSetValuesWithTransform: (
        animations: AnimationI[],
        property: string,
        transformer: (anim: AnimationI) => any
    ) => void
}

const MIXED = "__mixed__"

const EASE_TYPES = ["in", "out", "inOut"]
const EASE_STRENGTHS = ["extra-soft", "soft", "normal", "strong", "extra-strong"]

const toOptions = (values: string[]): CompactSelectOptionT[] =>
    values.map((value) => ({ value, label: value }))

/** A "Mixed" entry first while the selected animations disagree — shown, not choosable. */
const withMixed = (value: string, options: CompactSelectOptionT[]): CompactSelectOptionT[] =>
    value === MIXED ? [{ value: MIXED, label: "Mixed", disabled: true }, ...options] : options

/** Check if all selected animations share the same value for an ease property */
const getSharedEaseValue = (
    animations: AnimationI[],
    getter: (ease: EaseT) => string
): string | typeof MIXED => {
    if (animations.length <= 1) {
        const ease = animations[0]?.ease ?? defaultEase
        return getter(ease)
    }
    const values = animations.map((a) => getter(a.ease ?? defaultEase))
    return values.every((v) => v === values[0]) ? values[0] : MIXED
}

const Easing = ({
    animation,
    selectedAnimations,
    handleSetValue,
    handleSetValues,
    handleSetValuesWithTransform,
}: Props) => {
    const isMulti = selectedAnimations.length > 1
    const anims = isMulti ? selectedAnimations : [animation]

    const sharedStyle = getSharedEaseValue(anims, (e) => e.easeStyle)
    const sharedType = getSharedEaseValue(anims, (e) => e.easeType)
    const sharedStrength = getSharedEaseValue(anims, (e) => e.easeStrength)

    const handleSetEaseStyle = (value: any): void => {
        const ease = EaseStyles.find((e) => e.easeStyle === value)
        if (!ease) return
        if (isMulti) {
            handleSetValues(selectedAnimations, "ease", ease)
        } else {
            handleSetValue("ease", ease)
        }
    }

    const handleSetEaseProperty = (property: "easeType" | "easeStrength", value: string): void => {
        if (isMulti) {
            handleSetValuesWithTransform(selectedAnimations, "ease", (anim) => ({
                ...(anim.ease ?? defaultEase),
                [property]: value,
            }))
        } else {
            if (animation.ease) {
                handleSetValue(`ease.${property}`, value)
            } else {
                handleSetValue("ease", { ...defaultEase, [property]: value })
            }
        }
    }

    return (
        <ExpandableSection
            expanded={true}
            title={"Easing"}
            component={
                <Box display="flex" flexDirection="column" gap={1.5} width="100%" pb={0.5}>
                    {/* two to a row, like every other section */}
                    <PropertyRow>
                        <Box display="flex" gap={1}>
                            <Box flex={1} minWidth={0}>
                                <CompactSelect
                                    name="ease.easeStyle"
                                    value={sharedStyle}
                                    options={withMixed(
                                        sharedStyle,
                                        EaseStyles.map((ease: EaseT) => ({
                                            value: ease.easeStyle,
                                            label: ease.easeStyle,
                                        }))
                                    )}
                                    label="style"
                                    tooltip="Ease style"
                                    handleOnChange={(e) => handleSetEaseStyle(e.target.value)}
                                />
                            </Box>
                            <Box flex={1} minWidth={0}>
                                <CompactSelect
                                    name="ease.easeType"
                                    value={sharedType}
                                    options={withMixed(sharedType, toOptions(EASE_TYPES))}
                                    label="type"
                                    tooltip="Ease type"
                                    handleOnChange={(e) =>
                                        handleSetEaseProperty("easeType", e.target.value)
                                    }
                                />
                            </Box>
                        </Box>
                    </PropertyRow>
                    <PropertyRow>
                        <Box display="flex" gap={1}>
                            <Box flex={1} minWidth={0}>
                                <CompactSelect
                                    name="ease.easeStrength"
                                    value={sharedStrength}
                                    options={withMixed(sharedStrength, toOptions(EASE_STRENGTHS))}
                                    label="strength"
                                    tooltip="Ease strength"
                                    handleOnChange={(e) =>
                                        handleSetEaseProperty("easeStrength", e.target.value)
                                    }
                                />
                            </Box>
                            <Box flex={1} />
                        </Box>
                    </PropertyRow>
                </Box>
            }
        />
    )
}

export default Easing
